import { useState } from 'react';
import { useCashflows, useProfile } from '../../hooks/useApi';
import { formatCurrencyInput, extractNumericInput, formatAmount } from '../../utils/numberFormat';
import {
    CashFlowType,
    CashFlowCategory,
    DynamicCashFlow,
    Country,
    CASHFLOW_CATEGORY_LABELS,
    INCOME_CATEGORIES,
    EXPENSE_CATEGORIES,
    COUNTRY_CURRENCIES,
} from '../../types/api.types';
import styles from './CashflowPage.module.scss';

type ActiveTab = 'INCOME' | 'EXPENSE';

const COUNTRIES: { value: Country; label: string }[] = [
    { value: 'US', label: 'United States' },
    { value: 'INDIA', label: 'India' },
    { value: 'CAN', label: 'Canada' },
];

export function CashflowPage() {
    const [activeTab, setActiveTab] = useState<ActiveTab>('INCOME');
    const [showForm, setShowForm] = useState(false);
    const { cashflows, loading, error, refetch, addCashflow, deleteCashflow } = useCashflows();
    const { profile } = useProfile();
    const currentAge = profile?.currentAge;

    const filtered = cashflows.filter((cf) => cf.type === activeTab);
    const activeAtCurrentAge = currentAge
        ? filtered.filter((cf) => currentAge >= cf.startAge && currentAge <= cf.endAge)
        : filtered;
    const total = activeAtCurrentAge.reduce((sum, cf) => sum + cf.amount, 0);

    if (loading) {
        return (
            <div className={styles.page}>
                <p className={styles.loading}>Loading cashflows...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <p className={styles.error}>{error}</p>
                <button className={styles.retryButton} onClick={refetch}>
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.title}>Income & Expenses</h1>
                    <p className={styles.subtitle}>Manage your recurring and one-time cash flows.</p>
                </div>
                <div className={styles.headerActions}>
                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab} ${activeTab === 'INCOME' ? styles.active : ''}`}
                            onClick={() => setActiveTab('INCOME')}
                        >
                            Income
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'EXPENSE' ? styles.active : ''}`}
                            onClick={() => setActiveTab('EXPENSE')}
                        >
                            Expenses
                        </button>
                    </div>
                    <button className={styles.addButton} onClick={() => setShowForm(true)}>
                        + Add {activeTab === 'INCOME' ? 'Income' : 'Expense'}
                    </button>
                </div>
            </div>

            <div className={styles.totalCard}>
                <span className={styles.totalLabel}>
                    MONTHLY {activeTab} {currentAge ? `AT AGE ${currentAge}` : '(ALL)'}
                </span>
                <span className={styles.totalValue}>${new Intl.NumberFormat('en-US').format(total)}</span>
                <span className={styles.count}>
                    {activeAtCurrentAge.length} of {filtered.length} item(s) active
                </span>
            </div>

            {filtered.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No {activeTab.toLowerCase()} items yet.</p>
                    <button className={styles.addButton} onClick={() => setShowForm(true)}>
                        + Add your first {activeTab.toLowerCase()}
                    </button>
                </div>
            ) : (
                <div className={styles.cashflowList}>
                    {filtered.map((cf) => (
                        <CashflowCard key={cf.cashFlowId} cashflow={cf} onDelete={deleteCashflow} />
                    ))}
                </div>
            )}

            {showForm && (
                <AddCashflowModal
                    type={activeTab}
                    onClose={() => setShowForm(false)}
                    onAdded={() => {
                        setShowForm(false);
                        refetch();
                    }}
                    addCashflow={addCashflow}
                />
            )}
        </div>
    );
}

function CashflowCard({ cashflow, onDelete }: { cashflow: DynamicCashFlow; onDelete: (id: string) => void }) {
    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <span className={styles.cardCategory}>{CASHFLOW_CATEGORY_LABELS[cashflow.category]}</span>
                <span className={styles.cardCountry}>{cashflow.country}</span>
            </div>
            <div className={styles.cardBody}>
                <span className={styles.cardAmount}>{formatAmount(cashflow.amount, cashflow.currency)}</span>
                <span className={styles.cardMeta}>
                    Age {cashflow.startAge}–{cashflow.endAge}
                    {cashflow.growthRate > 0 && ` · ${cashflow.growthRate}% growth`}
                </span>
            </div>
            <button className={styles.deleteButton} onClick={() => onDelete(cashflow.cashFlowId)}>
                Remove
            </button>
        </div>
    );
}

interface AddCashflowModalProps {
    type: CashFlowType;
    onClose: () => void;
    onAdded: () => void;
    addCashflow: (cf: Omit<DynamicCashFlow, 'cashFlowId' | 'memberSub' | 'updatedAt'>) => Promise<DynamicCashFlow>;
}

function AddCashflowModal({ type, onClose, onAdded, addCashflow }: AddCashflowModalProps) {
    const categories = type === 'INCOME' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
    const [category, setCategory] = useState<CashFlowCategory>(categories[0]);
    const [country, setCountry] = useState<Country>('US');
    const currency = COUNTRY_CURRENCIES[country];
    const [amount, setAmount] = useState('');
    const [growthRate, setGrowthRate] = useState('3');
    const [startAge, setStartAge] = useState('35');
    const [endAge, setEndAge] = useState('65');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setFormError(null);
        try {
            await addCashflow({
                type,
                category,
                country,
                amount: Number(amount),
                currency,
                growthRate: Number(growthRate),
                startAge: Number(startAge),
                endAge: Number(endAge),
            });
            onAdded();
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>Add {type === 'INCOME' ? 'Income' : 'Expense'}</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        &times;
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.field}>
                        <label className={styles.label}>Category</label>
                        <select
                            className={styles.input}
                            value={category}
                            onChange={(e) => setCategory(e.target.value as CashFlowCategory)}
                        >
                            {categories.map((c) => (
                                <option key={c} value={c}>
                                    {CASHFLOW_CATEGORY_LABELS[c]}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label}>Country</label>
                        <select
                            className={styles.input}
                            value={country}
                            onChange={(e) => setCountry(e.target.value as Country)}
                        >
                            {COUNTRIES.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label className={styles.label}>Monthly Amount</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                className={styles.input}
                                value={formatCurrencyInput(amount, currency)}
                                onChange={(e) => setAmount(extractNumericInput(e.target.value))}
                                required
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>Annual Growth %</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={growthRate}
                                onChange={(e) => setGrowthRate(e.target.value)}
                                step={0.5}
                                min={0}
                                max={20}
                            />
                        </div>
                    </div>

                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label className={styles.label}>Start Age</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={startAge}
                                onChange={(e) => setStartAge(e.target.value)}
                                min={18}
                                max={90}
                                required
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>End Age</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={endAge}
                                onChange={(e) => setEndAge(e.target.value)}
                                min={Number(startAge)}
                                max={100}
                                required
                            />
                        </div>
                    </div>

                    {formError && <p className={styles.formError}>{formError}</p>}

                    <div className={styles.actions}>
                        <button type="button" className={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={saving}>
                            {saving ? 'Saving...' : 'Add'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
